import { useState, useEffect, useMemo } from "react";
import { Outlet, useOutlet, useSearchParams } from "react-router-dom";
import { CarouselStyles, TextStyles } from "styles/style";
import BaseView from "./BaseView";
import { api } from "supabase/database/api.ts";
import { Carousel } from "components/base/VideoCarousel";

export default function VideoView({
    title = null,
    list = null,
    table = null,
    url = null
}) {
    const [material, setMaterial] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const monthsPerPage = 3;
    const [_, setCapLength] = useState(63);
    const hasChild = useOutlet();

    // Get page and search from URL params
    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const currentSearch = searchParams.get('search') || '';

    // Fetch all data once
    useEffect(() => {
      async function prepMaterial() {
        if (list) {
          setMaterial(list);
        } else if (table) {
          const data = await api.contentQueries.getContentList({ table }, true);
          setMaterial(data);
        }
      }
      prepMaterial();
    }, [list, table]);

    // Handle search change
    const handleSearchChange = (newSearch) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (newSearch.trim()) {
                newParams.set('search', newSearch);
            } else {
                newParams.delete('search');
            }
            // Always reset to page 1 when searching
            if (newSearch.trim() !== currentSearch) {
                newParams.set('page', '1');
            }
            return newParams;
        }, { replace: true });
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('page', newPage.toString());
            return newParams;
        });
    };

    // Responsive title length
    useEffect(() => {
      const updateCapLength = () => {
        const width = window.innerWidth;
        if (width <= 640) setCapLength(63);
        else if (width <= 768) setCapLength(110);
        else setCapLength(180);
      };
      updateCapLength();
      window.addEventListener("resize", updateCapLength);
      return () => window.removeEventListener("resize", updateCapLength);
    }, []);

    const groupVideosByMonth = (videos) => {
      return videos.reduce((acc, video) => {
        if (!video.date) return acc; // skip if no date

        const dateObj = new Date(video.date);

        // Skip invalid dates
        if (isNaN(dateObj.getTime())) return acc;

        const monthYear = dateObj.toLocaleString('en-US', {
          month: 'long',
        });

        // Skip if month is "Invalid Date"
        if (monthYear === 'Invalid Date') return acc;

        if (!acc[monthYear]) {
          acc[monthYear] = [];
        }

        acc[monthYear].push(video);
        return acc;
      }, {});
    };

    // Filter videos and group by month
    const filteredAndGrouped = useMemo(() => {
      const filtered = material.filter((item) =>
        item.title?.toLowerCase().includes(currentSearch.toLowerCase())
        || item.date?.toLowerCase().includes(currentSearch.toLowerCase())
      );
      return groupVideosByMonth(filtered);
    }, [material, currentSearch]);

    // Paginate months
    const paginatedMonths = useMemo(() => {
      const monthEntries = Object.entries(filteredAndGrouped);
      const start = (currentPage - 1) * monthsPerPage;
      return monthEntries.slice(start, start + monthsPerPage);
    }, [filteredAndGrouped, currentPage]);

    const totalVideos = useMemo(() => {
      return Object.values(filteredAndGrouped).flat().length;
    }, [filteredAndGrouped]);

    const totalMonths = Object.keys(filteredAndGrouped).length;

    return (
        <>
        {hasChild ? (
            <Outlet />
        ) : (
            <BaseView
                title={title}
                listCount={totalMonths}
                search={currentSearch}
                onSearchChange={handleSearchChange}
                page={currentPage}
                onPageChange={handlePageChange}
                pageSize={monthsPerPage}
            >
                {paginatedMonths.length > 0 && (
                    <>
                        {paginatedMonths.map(([month, videos]) => (
                            <div key={month}>
                                <CarouselStyles.CarouselPost>
                                    <TextStyles.Info>
                                        <TextStyles.Title>{month}</TextStyles.Title>
                                    </TextStyles.Info>
                                </CarouselStyles.CarouselPost>
                                <Carousel videoList={videos} month={month} />
                            </div>
                        ))}
                    </>
                )}
            </BaseView>
        )}
        </>
    )
}
